/**
 * Le classement de la semaine des chatteurs (Kayla, 07-10-2026) — la règle,
 * pure, seule copie : Convex l'applique (rangs, montées, prime du lundi) et le
 * cockpit l'importe pour l'afficher.
 *
 * Ses mots : « un leaderboard de la semaine parmi les chatteurs, calculé par
 * rapport à celui qui a fait le plus d'argent ». 3 % de TOUT le CA de la
 * semaine (Cyrano compris — son choix) reversés aux trois premiers, 50/30/20.
 * « Ils ne doivent pas savoir que ça représente quel pourcentage » : on montre
 * la prime de chaque place, jamais le 3 % ni le CA total.
 *
 * La semaine = le cycle de paie, lundi 12h00 → lundi 12h00 (Paris) : c'est
 * Cyrano qui la découpe (`/internal/classement/semaine`), avec la même coupure
 * que le Portefeuille.
 */

/** La part du CA de la semaine qui fait la cagnotte. */
export const CAGNOTTE_POUR_CENT = 3;
/** Comment la cagnotte se partage entre le 1er, le 2e et le 3e. */
export const PARTS_DU_PODIUM = [50, 30, 20] as const;

/** La prime de chaque marche du podium, en cents, pour un CA donné. Arrondi
 *  vers le bas : on ne verse jamais un cent de plus que la cagnotte. */
export function primesDuPodium(totalCents: number): [number, number, number] {
  const cagnotte = Math.max(0, totalCents) * (CAGNOTTE_POUR_CENT / 100);
  const [a, b, c] = PARTS_DU_PODIUM.map((p) => Math.floor((cagnotte * p) / 100));
  return [a, b, c];
}

export type Vendeur = {
  workosUserId: string;
  brutCents: number;
  /** ISO UTC de sa dernière vente de la semaine (Cyrano) — le départage. */
  derniereVente?: string | null;
};
export type Ligne = { workosUserId: string; brutCents: number; rang: number };

/**
 * Le classement : du plus gros brut au plus petit.
 *
 * ⚖️ Deux brut ÉGAUX au cent = la MÊME place (« 1, 1, 3 ») — Kayla, 07-10-2026,
 * Tito et yuki rak à 248,40 $ chacun : « je trouve ça injuste qu'il y en ait
 * un qui va avoir une plus grosse commission que l'autre ». La prime se
 * partage alors (`primesDesLignes`). L'ordre d'affichage entre ex æquo suit
 * l'heure de la dernière vente puis l'identifiant — il ne change donc jamais
 * d'une lecture à l'autre (l'ordre de Postgres, lui, peut changer), mais il ne
 * décide plus de rien.
 * `classables` : les chatteurs qui concourent (la CEO, un manager ne
 * concourent pas — cf `estClassable`).
 */
export function classer(vendeurs: Vendeur[], classables: Set<string>): Ligne[] {
  const tries = vendeurs
    .filter((v) => v.brutCents > 0 && classables.has(v.workosUserId))
    .sort(
      (a, b) =>
        b.brutCents - a.brutCents ||
        (a.derniereVente ?? "￿").localeCompare(b.derniereVente ?? "￿") ||
        a.workosUserId.localeCompare(b.workosUserId),
    );
  const out: Ligne[] = [];
  tries.forEach((v, i) => {
    const precedent = out[i - 1];
    const rang = precedent && precedent.brutCents === v.brutCents ? precedent.rang : i + 1;
    out.push({ workosUserId: v.workosUserId, brutCents: v.brutCents, rang });
  });
  return out;
}

/**
 * La prime de chacun, en cents. Les ex æquo se partagent ÉGALEMENT les primes
 * des places qu'ils occupent ensemble : deux 1ers prennent (1re + 2e) / 2
 * chacun, et le suivant est 3e ; deux 3es se partagent la seule 3e prime.
 * Arrondi vers le bas : on ne verse jamais un cent de plus que la cagnotte.
 * Rend une prime par ligne, dans l'ordre des lignes (0 hors podium).
 */
export function primesDesLignes(lignes: Ligne[], primes: readonly number[]): number[] {
  return lignes.map((l) => {
    const groupe = lignes.filter((x) => x.rang === l.rang).length;
    let somme = 0;
    for (let place = l.rang; place < l.rang + groupe; place++) somme += primes[place - 1] ?? 0;
    return Math.floor(somme / groupe);
  });
}

/**
 * Qui concourt : un CHATTEUR — même reconnaissance que le kiosque
 * (`src/shell/render/kioskRoles.ts::isChatterLike`) : le rôle `chatter`, ou
 * tout rôle qui chatte sans gérer les créatrices. CEO, manager et opérateur
 * gèrent les créatrices : ils ne prennent pas la prime de l'équipe.
 */
export function estClassable(roleSlug: string | null, permissions: readonly string[]): boolean {
  if (roleSlug === "chatter") return true;
  return permissions.includes("conversations.chat") && !permissions.includes("creators.view");
}

export type Montee = { workosUserId: string; de: number | null; a: number };

/**
 * Qui vient de GRIMPER : sa place est meilleure qu'au dernier relevé, ou il
 * n'était pas encore classé — sa première vente de la semaine l'y fait entrer,
 * et ça s'annonce aussi (Kayla, 08-10-2026). Ceux qui reculent (parce qu'un
 * autre est passé devant) ne disent rien.
 *
 * Sortir d'un ex æquo par le haut est une montée (Kayla, 08-10-2026 : yuki rak
 * 1re ex æquo avec Tito, puis 1re seule, sans un mot dans #rang) : la place ne
 * change pas, mais il n'y a plus personne à côté.
 */
export function montees(avant: Ligne[], apres: Ligne[]): Montee[] {
  const ancien = new Map(avant.map((l) => [l.workosUserId, l.rang]));
  const partage = (lignes: Ligne[], rang: number) => lignes.filter((x) => x.rang === rang).length > 1;
  const out: Montee[] = [];
  for (const l of apres) {
    const de = ancien.get(l.workosUserId) ?? null;
    const grimpe =
      de === null || l.rang < de || (l.rang === de && partage(avant, de) && !partage(apres, l.rang));
    if (grimpe) out.push({ workosUserId: l.workosUserId, de, a: l.rang });
  }
  return out;
}

/** « 1re », « 2e », « 3e »… — la place, à la française. */
export function place(rang: number): string {
  return rang === 1 ? "1re" : `${rang}e`;
}

const MEDAILLES: Record<number, string> = { 1: "🥇", 2: "🥈", 3: "🥉" };

/** Le petit mot du salon #rang. Court : c'est une notification, pas une annonce.
 *  `exAequo` : la place est partagée avec quelqu'un d'autre. `entree` : sa
 *  première vente de la semaine, il n'était pas encore classé. */
export function messageMontee(mention: string, rang: number, exAequo = false, entree = false): string {
  const medaille = MEDAILLES[rang] ?? "🚀";
  const suffixe = exAequo ? " ex æquo" : "";
  if (entree && rang > 3)
    return `${medaille} Félicitations ${mention}, tu entres dans le classement de la semaine à la **${place(rang)} place${suffixe}** !`;
  if (rang === 1) return `${medaille} Félicitations ${mention}, tu prends la **1re place${suffixe}** du classement de la semaine !`;
  return `${medaille} Félicitations ${mention}, tu montes à la **${place(rang)} place${suffixe}** du classement de la semaine !`;
}

const MOIS = [
  "janvier", "février", "mars", "avril", "mai", "juin",
  "juillet", "août", "septembre", "octobre", "novembre", "décembre",
];

function jourParis(iso: string): { jour: number; mois: number } {
  const parts = new Intl.DateTimeFormat("fr-FR", {
    timeZone: "Europe/Paris",
    day: "numeric",
    month: "numeric",
  }).formatToParts(new Date(iso));
  const val = (t: string) => Number(parts.find((p) => p.type === t)?.value);
  return { jour: val("day"), mois: val("month") - 1 };
}

/** « du lundi 5 au lundi 12 octobre » — la semaine, en heure de Paris. */
export function libelleSemaine(debutIso: string, finIso: string): string {
  const a = jourParis(debutIso);
  const b = jourParis(finIso);
  const moisA = a.mois === b.mois ? "" : ` ${MOIS[a.mois]}`;
  return `du lundi ${a.jour}${moisA} au lundi ${b.jour} ${MOIS[b.mois]}`;
}

function dollars(cents: number): string {
  const entier = Math.floor(cents / 100);
  const reste = cents % 100;
  const milliers = entier.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  return reste === 0 ? `${milliers} $` : `${milliers},${String(reste).padStart(2, "0")} $`;
}

/** Un gagnant du podium, tel que le message l'annonce. `commissionCents` :
 *  ce que ses ventes de la semaine lui ont rapporté (Cyrano le chiffre avec le
 *  calcul de la paie) — absent ou `null` si inconnu, et le message ne montre
 *  alors que la prime, jamais un « 0 $ » qui mentirait. */
export type GagnantAnnonce = {
  mention: string;
  rang: number;
  primeCents: number;
  commissionCents?: number | null;
  ventes?: number | null;
};

/**
 * Le texte qui accompagne l'image du podium, le lundi.
 *
 * Kayla, 11-10-2026 : un `@everyone`, et ce que chacun des trois a VRAIMENT
 * gagné — ses ventes plus sa prime — « pour donner envie aux autres d'être
 * dans les tops ». Le ping se décide chez l'appelant (`allowed_mentions`) :
 * ici, seulement le texte.
 */
export function messagePodium(gagnants: GagnantAnnonce[]): string {
  const lignes = [...gagnants]
    .sort((a, b) => a.rang - b.rang)
    .flatMap((g) => {
      const medaille = MEDAILLES[g.rang] ?? "🏅";
      if (g.commissionCents == null) return [`${medaille} ${g.mention} — **+${dollars(g.primeCents)}** de prime`];
      const ventes = g.ventes == null ? "ses ventes" : `${g.ventes} vente${g.ventes > 1 ? "s" : ""}`;
      return [
        `${medaille} ${g.mention} — **${dollars(g.commissionCents + g.primeCents)}** gagnés cette semaine`,
        `-# ${ventes} (+${dollars(g.commissionCents)}) + ${dollars(g.primeCents)} de prime`,
      ];
    });
  return [
    "@everyone",
    "🏆 **Le podium de la semaine est tombé !**",
    "",
    ...lignes,
    "",
    "Tout est déjà dans leurs Portefeuilles 💸 Nouvelle semaine, compteurs à zéro : la prochaine place sur le podium peut être la tienne 🔥",
  ].join("\n");
}
